import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { RefreshCw, Save, Filter, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MISMappings() {
  const [mappings, setMappings] = useState([]);
  const [plHeads, setPlHeads] = useState([]);
  const [plSubheads, setPlSubheads] = useState({});
  const [bsHeads, setBsHeads] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState("all"); // all, group, ledger, overrides
  const [filterCategory, setFilterCategory] = useState("all"); // all, PL, BS
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingChanges, setPendingChanges] = useState({});

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mis/mappings`);
      setMappings(res.data.mappings || []);
      setPlHeads(res.data.pl_heads || []);
      setPlSubheads(res.data.pl_subheads || {});
      setBsHeads(res.data.bs_heads || {});
    } catch (err) {
      toast.error("Failed to load mappings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const getHeadOptions = (category) => {
    if (category === "PL") {
      return plHeads;
    }
    if (category === "BS") {
      const all = [];
      Object.values(bsHeads).forEach(arr => {
        if (Array.isArray(arr)) all.push(...arr);
      });
      return all;
    }
    return [];
  };

  const getSubheadOptions = (category, head) => {
    if (category === "PL") {
      return plSubheads[head] || [];
    }
    return [];
  };

  const handleChange = (mapping, field, value) => {
    const key = `${mapping.type}:${mapping.name}`;
    const current = pendingChanges[key] || { ...mapping };
    current[field] = value;
    if (field === "category") {
      current.mis_head = "";
      current.mis_subhead = "";
    }
    if (field === "mis_head") {
      current.mis_subhead = "";
    }
    setPendingChanges({ ...pendingChanges, [key]: current });
  };

  const getDisplayValue = (mapping, field) => {
    const key = `${mapping.type}:${mapping.name}`;
    if (pendingChanges[key]) {
      return pendingChanges[key][field];
    }
    return mapping[field];
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleSave = async () => {
    const updates = Object.values(pendingChanges).map(m => ({
      name: m.name,
      type: m.type,
      category: m.category,
      mis_head: m.mis_head,
      mis_subhead: m.mis_subhead || "",
    }));

    if (updates.length === 0) return;

    setSaving(true);
    try {
      await axios.post(`${API}/mis/mappings`, updates);
      toast.success(`Updated ${updates.length} mapping(s)`);
      setPendingChanges({});
      fetchMappings();
    } catch (err) {
      toast.error("Failed to save mappings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all mappings to auto-generated defaults? Any manual changes will be lost.")) return;
    setSaving(true);
    try {
      await axios.post(`${API}/mis/mappings/reset`);
      toast.success("Mappings reset to defaults");
      setPendingChanges({});
      fetchMappings();
    } catch (err) {
      toast.error("Failed to reset mappings");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Re-generate all mappings from COA data? This will overwrite all changes.")) return;
    setSaving(true);
    try {
      await axios.post(`${API}/mis/mappings/generate`);
      toast.success("Mappings regenerated from COA");
      setPendingChanges({});
      fetchMappings();
    } catch (err) {
      toast.error("Failed to regenerate mappings");
    } finally {
      setSaving(false);
    }
  };

  const filtered = mappings.filter((m) => {
    if (filterType === "group" && m.type !== "group") return false;
    if (filterType === "ledger" && m.type !== "ledger") return false;
    if (filterType === "overrides" && !m.is_override) return false;
    if (filterCategory === "PL" && m.category !== "PL") return false;
    if (filterCategory === "BS" && m.category !== "BS") return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return m.name.toLowerCase().includes(s) ||
        m.original_parent?.toLowerCase().includes(s) ||
        m.mis_head?.toLowerCase().includes(s) ||
        m.mis_subhead?.toLowerCase().includes(s);
    }
    return true;
  });

  const overrideCount = mappings.filter(m => m.is_override).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="mappings-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="mis-mappings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="mappings-title">
          Reclassification Manager
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {mappings.length} mapping(s) total | {overrideCount} manually reclassified
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={saving} data-testid="btn-regenerate">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate from COA
        </Button>
        <Button size="sm" variant="outline" onClick={handleReset} disabled={saving} data-testid="btn-reset">
          Reset to Defaults
        </Button>
        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="btn-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> Save {Object.keys(pendingChanges).length} Change(s)
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Type:</span>
        {[
          { id: "all", label: "All" },
          { id: "group", label: "Groups" },
          { id: "ledger", label: "Ledgers" },
          { id: "overrides", label: `Overrides (${overrideCount})` },
        ].map((f) => (
          <Button key={f.id} size="sm" variant={filterType === f.id ? "default" : "outline"}
            onClick={() => setFilterType(f.id)} className="text-xs h-7 px-3" data-testid={`filter-type-${f.id}`}>
            {f.label}
          </Button>
        ))}

        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-3">Category:</span>
        {[
          { id: "all", label: "All" },
          { id: "PL", label: "P&L" },
          { id: "BS", label: "Balance Sheet" },
        ].map((f) => (
          <Button key={f.id} size="sm" variant={filterCategory === f.id ? "default" : "outline"}
            onClick={() => setFilterCategory(f.id)} className="text-xs h-7 px-3" data-testid={`filter-cat-${f.id}`}>
            {f.label}
          </Button>
        ))}

        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="ml-3 px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 w-48"
          data-testid="mappings-search"
        />
      </div>

      {/* Table */}
      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold w-10">Type</TableHead>
                <TableHead className="text-xs font-semibold min-w-[200px]">Name</TableHead>
                <TableHead className="text-xs font-semibold min-w-[150px]">Original Group</TableHead>
                <TableHead className="text-xs font-semibold w-8 text-center"></TableHead>
                <TableHead className="text-xs font-semibold min-w-[80px]">Category</TableHead>
                <TableHead className="text-xs font-semibold min-w-[180px]">MIS Head</TableHead>
                <TableHead className="text-xs font-semibold min-w-[180px]">MIS Subhead</TableHead>
                <TableHead className="text-xs font-semibold w-16 text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((m, i) => {
                const key = `${m.type}:${m.name}`;
                const isPending = !!pendingChanges[key];
                const cat = getDisplayValue(m, "category");
                const head = getDisplayValue(m, "mis_head");
                const subhead = getDisplayValue(m, "mis_subhead");

                return (
                  <TableRow key={`${m.type}-${m.name}`} className={`${isPending ? "bg-blue-50/50" : m.is_override ? "bg-amber-50/30" : ""}`}>
                    <TableCell>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${m.type === "group" ? "bg-slate-200 text-slate-700" : "bg-blue-100 text-blue-700"}`}>
                        {m.type === "group" ? "GRP" : "LDG"}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm font-medium text-slate-900">{m.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{m.original_parent || "-"}</TableCell>
                    <TableCell className="text-center"><ArrowRight className="w-3.5 h-3.5 text-slate-300 mx-auto" /></TableCell>
                    <TableCell>
                      <select
                        value={cat}
                        onChange={(e) => handleChange(m, "category", e.target.value)}
                        className="text-xs border border-slate-200 rounded px-1.5 py-1 focus:ring-1 focus:ring-slate-400 w-full bg-white"
                        data-testid={`mapping-cat-${i}`}
                      >
                        <option value="PL">P&L</option>
                        <option value="BS">BS</option>
                      </select>
                    </TableCell>
                    <TableCell>
                      <select
                        value={head}
                        onChange={(e) => handleChange(m, "mis_head", e.target.value)}
                        className="text-xs border border-slate-200 rounded px-1.5 py-1 focus:ring-1 focus:ring-slate-400 w-full bg-white"
                        data-testid={`mapping-head-${i}`}
                      >
                        <option value="">-- Select --</option>
                        {getHeadOptions(cat).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      {cat === "PL" ? (
                        <select
                          value={subhead}
                          onChange={(e) => handleChange(m, "mis_subhead", e.target.value)}
                          className="text-xs border border-slate-200 rounded px-1.5 py-1 focus:ring-1 focus:ring-slate-400 w-full bg-white"
                          data-testid={`mapping-subhead-${i}`}
                        >
                          <option value="">-- Select --</option>
                          {getSubheadOptions(cat, head).map((sh) => (
                            <option key={sh} value={sh}>{sh}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-slate-400">N/A</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {isPending ? (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Pending</span>
                      ) : m.is_override ? (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Manual</span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Auto</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                    No mappings found matching filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 mt-3">
        Showing {filtered.length} of {mappings.length} mappings.
        {hasChanges && <span className="text-blue-600 ml-2">You have unsaved changes.</span>}
      </p>
    </div>
  );
}
